import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Linkedin, Instagram, ArrowUpRight } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { openCalendly } from '../lib/calendly';

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (href) => {
    if (href === 'calendly') { openCalendly(); return; }
    if (href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const el = document.querySelector(href);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        return;
      }
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(href);
    }
  };

  const footerLinks = {
    Services: [
      { label: 'Administrative Support', href: '#services' },
      { label: 'Customer Service', href: '#services' },
      { label: 'Technical Support', href: '#services' },
      { label: 'Creative Services', href: '#services' },
    ],
    Company: [
      { label: 'Why Us', href: '#why-us' },
      { label: 'Our Work', href: '#portfolio' },
      { label: 'Testimonials', href: '#testimonials' },
      { label: 'Pricing', href: '#pricing' },
    ],
    Resources: [
      { label: 'Blog', href: '/blog' },
      { label: 'FAQ', href: '#faq' },
      { label: 'Contact Us', href: '#contact' },
      { label: 'Book a Call', href: 'calendly' },
    ],
  };

  const socials = [
    { icon: Facebook, label: 'Facebook' },
    { icon: Twitter, label: 'Twitter' },
    { icon: Linkedin, label: 'LinkedIn' },
    { icon: Instagram, label: 'Instagram' },
  ];

  return (
    <footer className="bg-[#0D1612] relative overflow-hidden border-t border-white/[0.06]" data-testid="site-footer">
      {/* Massive CTA */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-16">
        <motion.button
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          onClick={() => scrollToSection('#contact')}
          className="group block w-full text-left"
          data-testid="footer-cta"
        >
          <div className="flex items-center flex-wrap gap-4">
            <span className="font-display text-[15vw] lg:text-[13rem] leading-[0.85] font-black text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-400 group-hover:to-lime-400 transition-all duration-500">
              LET&rsquo;S TALK
            </span>
            <span className="w-20 h-20 lg:w-28 lg:h-28 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-500">
              <ArrowUpRight className="w-10 h-10 lg:w-14 lg:h-14 text-[#04120C]" />
            </span>
          </div>
        </motion.button>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 pt-16 border-t border-white/[0.06]">
          <div className="lg:col-span-2">
            <img src="/assets/oh-logo.png" alt="OutsourcedHub" className="h-20 w-auto mb-6" data-testid="footer-logo" />
            <p className="text-slate-400 leading-relaxed max-w-sm mb-6">
              Elite virtual assistants matched to your workflow, culture, and goals. Reclaim your time, scale with confidence.
            </p>
            <div className="text-slate-400 space-y-1 text-sm">
              <div>info@outsourcedhub.com</div>
              <div>+1 562-352-3244</div>
              <div>Manila, Philippines</div>
            </div>
          </div>

          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-white font-display font-bold mb-5">{category}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    <button onClick={() => scrollToSection(link.href)} className="text-slate-400 hover:text-emerald-400 transition-colors duration-300 text-sm">
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-10 mt-10 border-t border-white/[0.06] flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-slate-500 text-sm">&copy; 2024 OutsourcedHub. All Rights Reserved.</div>
          <div className="flex items-center gap-3">
            {socials.map((s) => {
              const Icon = s.icon;
              return (
                <a key={s.label} href="#" aria-label={s.label} className="w-10 h-10 rounded-full bg-white/[0.04] border border-white/[0.06] hover:bg-emerald-500 hover:border-emerald-500 flex items-center justify-center text-slate-400 hover:text-[#04120C] transition-all duration-300" data-testid={`social-${s.label.toLowerCase()}`}>
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
